import React, { useContext, useEffect, useState } from 'react'
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid'
import { useParams, useSearchParams } from 'react-router-dom'

import { DEFAULT_LIMIT } from '../../components/datagrid/EdifyDataGrid'
import { getAllFormCategories, getConnectedSubmissionDataBy, getOneForm } from '../../../data/data'
import {
  getFormFilters,
  getSubmissionsWithQueryObject,
  SubmissionQueryField,
  triggerSubmissionNotifications,
} from '../../../domain/domain'
import { ICategory, IForm } from '../../../domain/interfaces/IForm'
import { IFormFilter } from '../../../domain/interfaces/IFormHeader'
import { ISubmission } from '../../../domain/interfaces/ISubmission'
import { normalizeDynamicHeadersForTableView } from '../../../core/utils/formio-formatters/FormIOFormatter'
import { SuccessToast, ErrorToast } from '../../../core/utils/toast-notifications/ToastNotifications'
import useQuery from '../../../core/hooks/useQuery'

export interface SubmissionsPageContextType {
  clearError?: () => void
  clearFilters: (reload: boolean) => void
  fetchSubmissions: (skip: number, query: SubmissionQueryField) => void
  // form?: IForm | undefined
  form: IForm
  formError?: string
  // formHeaders: IFormHeader[] | undefined
  isLoading: boolean
  isLoadMore: boolean
  isSearching: boolean
  notifyIds: string[]
  loadMore: (paginationModel: GridPaginationModel) => void
  queryHelper: Record<string, any>
  reloadForm: () => void
  reloadPage: () => void
  reloadSubmissions: () => void
  setTotalCount?: (value: number) => void
  sortClickedHandler: (sorter: GridSortModel) => void
  submissionError?: string
  submissions?: ISubmission[] | undefined
  allSubmissions: ISubmission[]
  totalCount: number
  // for form settings
  categories: ICategory[]
  setCategories: (cat: ICategory[]) => void
  loadingCategories: boolean
  categoriesError: string | undefined
  setForm: (form: IForm) => void
  normalizedDynamicHeaders: any
  formFilters: IFormFilter[]
  formFiltersError: string | undefined
  formFiltersLoading: boolean
  hasCreatedAtHeader: boolean
  errorObj?: any | undefined
  noFilters: () => boolean
  onNotify: (id: string) => void
  onCopy: (id: string) => void
  connectedData: any
  subscriptionFilterDate: string | null
}

const SubmissionPageContext = React.createContext<SubmissionsPageContextType | null>(null)
SubmissionPageContext.displayName = 'SubmissionPageContext'

interface SubmissionsPageVMProps {
  children: React.ReactNode
  formIdProp?: string
  formFromOrgProvider?: IForm
}

export const SubmissionsPageViewModel: React.FC<SubmissionsPageVMProps> = ({
  children,
  formIdProp,
  formFromOrgProvider,
}) => {
  let { formId } = useParams()
  // for submission modal
  if (formIdProp) {
    formId = formIdProp
  }

  // if webHeaders are set createdAt must be an option, if it is not use the first header as the default sort
  const webHeaders = formFromOrgProvider?.webHeaders ?? []
  const hasCreatedAtHeader = webHeaders.some(x => x.key === 'createdAt') ? true : false
  const [searchParams] = useSearchParams()
  const subscriptionFilterDate = searchParams.get('subscriptionFilterDate')

  const queryHelper = useQuery({
    skip: 0,
    limit: DEFAULT_LIMIT,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    filters: [],
    ...(subscriptionFilterDate && { subscriptionFilterDate }),
    filterId: undefined,
  })

  const { query, getQueryStringFromQuery, setNew, defaultQuery } = queryHelper

  const [isLoading, setIsLoading] = useState({
    initialLoad: true,
    loadMore: false,
    search: false,
    notifyIds: [] as string[],
  })

  //// Form state
  const [form, setForm] = useState<IForm>(formFromOrgProvider!)
  // const [formHeaders, setFormHeaders] = useState<IFormHeader[] | undefined>(undefined)
  // Form state
  const [formError, setFormError] = useState<string | undefined>(undefined)
  const [errorObj, setErrorObj] = useState<any | undefined>(undefined)

  const [formFilters, setFormFilters] = useState<IFormFilter[]>([])
  const [connectedData, setConnectedData] = useState<any[]>([])

  const [formFiltersError, setFormFiltersError] = useState<string | undefined>(undefined)

  const [formFiltersLoading, setFormFiltersLoading] = useState<boolean>(true)
  // Submission state
  const [totalCount, setTotalCount] = useState<number>(0)
  const [submissionError, setSubmissionError] = useState<string | undefined>(undefined)
  const [submissions, setSubmissions] = useState<ISubmission[] | undefined>(undefined)

  const [allSubmissions, setAllSubmissions] = useState<ISubmission[]>([])

  // category state
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [categories, setCategories] = useState<ICategory[]>([])
  const [categoriesError, setCategoriesError] = useState<string | undefined>(undefined)

  const [normalizedDynamicHeaders, setNormalizedDynamicHeaders] = useState<any>(undefined)

  const clearFilters = async (reload = false) => {
    // reloads data but doesn't clear inputs
    if (reload) {
      fetchSubmissions(0, { ...defaultQuery, filterId: '', filters: [] })
    }
  }

  const loadCatagories = async () => {
    setLoadingCategories(true)
    setCategoriesError(undefined)
    const res = await getAllFormCategories()
    setLoadingCategories(false)
    if (res.right) {
      setCategories(res.right.data)
      return
    }
    setCategoriesError('Error')
  }

  const getConnectedData = async (submissions: ISubmission[], normalizedDynamicHeaders: any) => {
    if (!submissions && normalizedDynamicHeaders) return
    const childFormIds = Array.from(
      new Set(
        normalizedDynamicHeaders
          .filter((header: any) => header.childFormId != null)
          .map((header: any) => header.childFormId),
      ),
    )
    const parentSubmissionIds = submissions.map(s => s.id)
    const apiPromises = childFormIds.map((id: any) => {
      return getConnectedSubmissionDataBy({
        formId: id ?? '',
        submissionIds: parentSubmissionIds,
      })
    })

    const results = await Promise.all(apiPromises)
    const resultsData = results.map(result => {
      return result.right
    })
    setConnectedData(resultsData)
    return resultsData.map((rd, i) => {
      return {
        childFormId: childFormIds[i],
        formId,
        data: rd,
      }
    })
  }

  // handles loading submissions or corrective actions
  const fetchSubmissions = async (fetchSkip: number, query: Record<string, any>) => {
    setIsLoading(oldState => {
      return { ...oldState, loadMore: true }
    })
    setSubmissionError(undefined)
    const newQuery = { ...query, skip: fetchSkip }
    const res = await getSubmissionsWithQueryObject(formId!, newQuery)

    if (res.isLeft()) {
      // submissionError handles both submissions and corrective actions
      setSubmissionError(res.left?.message)
      setErrorObj(res.left)
      // if error set back to what is was
      setNew(query)
      setIsLoading(oldState => {
        return { ...oldState, initialLoad: false, loadMore: false }
      })
      return
    }
    const newSubmissions: ISubmission[] = res.right?.data ?? []
    setSubmissions(newSubmissions as ISubmission[])
    // keep track of all loaded submissions so pdf download has access to them

    const uniqueSubmissions = newSubmissions.filter(ns => {
      // A submission is unique if NONE of the existing submissions have the same ID
      return !allSubmissions.some(as => {
        return ns.id === as.id
      })
    })
    // was using allSubmissions
    setAllSubmissions([...allSubmissions, ...uniqueSubmissions])
    setSubmissions(newSubmissions)
    const connectedFormData = await getConnectedData(newSubmissions, normalizedDynamicHeaders ?? [])
    setIsLoading(oldState => {
      return { ...oldState, initialLoad: false, loadMore: false }
    })
    setConnectedData(connectedFormData ?? [])
    setTotalCount(res.right?.total_count || 0)
    setNew(newQuery)
  }

  const loadMore = (paginationModel: GridPaginationModel) => {
    const fetchSkip = paginationModel.page * paginationModel.pageSize
    fetchSubmissions(fetchSkip, query)
  }

  const getAllSubmissions = async () => {
    setSubmissions([])
    fetchSubmissions(0, queryHelper.query)
  }

  const sortClickedHandler = (sorter: GridSortModel) => {
    // if (isLoading.initialLoad) return
    const { field, sort } = sorter[0]
    const newQuery = {
      ...query,
      skip: 0,
      sortBy: field,
      sortOrder: sort,
      // sortOrder: sort?.toUpperCase(),
    }
    fetchSubmissions(0, newQuery)
  }

  const loadFormAndSubmissions = async () => {
    setForm(formFromOrgProvider!)
    const normalizedDynamicHeaders = normalizeDynamicHeadersForTableView(formFromOrgProvider!)

    setNormalizedDynamicHeaders(normalizedDynamicHeaders)
  }
  useEffect(() => {
    if (!normalizedDynamicHeaders) return
    setSubmissionError(undefined)
    setFormError(undefined)

    getAllSubmissions()
  }, [normalizedDynamicHeaders])

  const loadFormFilters = async () => {
    if (!form) return
    const res = await getFormFilters(form.id)
    setFormFiltersLoading(false)
    if (res.isLeft()) {
      setFormFiltersError(res.left?.message)
      return
    }
    setFormFilters(res.right!)
  }
  const loadForm = async () => {
    setIsLoading(oldState => {
      return { ...oldState, initialLoad: true }
    })
    const res = await getOneForm(formId!)
    setIsLoading(oldState => {
      return { ...oldState, initialLoad: false }
    })
    if (res.left) {
      setFormError(res.left.message ?? 'Error Loading Form')
      return
    }
    if (res.right) {
      setForm(res.right)
    }
  }
  const initialLoad = async () => {
    if (!formFromOrgProvider) {
      await loadForm()
    }
    loadCatagories()
    loadFormAndSubmissions()
    loadFormFilters()
  }
  useEffect(() => {
    initialLoad()
  }, [formId, formIdProp, formFromOrgProvider])

  const reloadPage = () => {
    loadFormAndSubmissions()
    loadFormFilters()
  }

  const onClearError = () => {
    setFormError(undefined)
  }

  const noFilters = () => {
    return JSON.stringify(query) == JSON.stringify(defaultQuery)
  }

  const onNotify = async (id: string) => {
    setIsLoading(oldState => {
      const notifyIds = oldState.notifyIds
      notifyIds.push(id)
      return { ...oldState, notifyIds }
    })

    const res = await triggerSubmissionNotifications(id)
    if (res.left) {
      ErrorToast({
        title: 'Error in sending notifications!',
        subtitle: 'Unable to schedule notifications, please try again later.',
      })
    } else {
      SuccessToast({
        title: 'Notifications sent!',
        subtitle: 'Notifications have been sent to the users.',
      })
    }

    setIsLoading(oldState => {
      let notifyIds = oldState.notifyIds

      //remove id from notifyIds
      notifyIds = notifyIds.filter(x => x !== id)

      return { ...oldState, notifyIds }
    })
  }

  const onCopy = (id: string) => {
    setIsLoading(oldState => {
      return { ...oldState, notify: true }
    })
  }

  return (
    <SubmissionPageContext.Provider
      value={{
        subscriptionFilterDate,
        submissions,
        allSubmissions,
        isLoading: isLoading.initialLoad,
        isLoadMore: isLoading.loadMore,
        isSearching: isLoading.search,
        notifyIds: isLoading.notifyIds,
        loadMore,
        form,
        formError,
        submissionError,
        clearError: onClearError,
        reloadForm: loadFormAndSubmissions,
        reloadSubmissions: getAllSubmissions,
        reloadPage,
        // formHeaders,
        clearFilters,
        fetchSubmissions,
        queryHelper,
        totalCount,
        sortClickedHandler,
        categories,
        setCategories,
        loadingCategories,
        categoriesError,
        setForm,
        normalizedDynamicHeaders,
        formFilters,
        formFiltersError,
        formFiltersLoading,
        hasCreatedAtHeader,
        errorObj,
        noFilters,
        onNotify,
        onCopy,
        connectedData,
      }}
    >
      {children}
    </SubmissionPageContext.Provider>
  )
}
export default SubmissionPageContext

export function useSubmissionsPageViewModel() {
  const context = useContext(SubmissionPageContext)
  if (!context) {
    throw new Error('useSubmissionsPageViewModel must be used within a SubmissionsPageViewModel')
  }
  return context
}
